import { useState, useEffect, useRef, useCallback } from 'react';
import { Map } from 'ol';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import { circular } from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { fromLonLat } from 'ol/proj';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import {
  DISPLAY_SCALE_CHANGE_EVENT,
  getInitialMapZoom,
  type DisplayScaleSnapshot,
} from '../mapDisplayScale';

interface UseMapControlsProps {
  mapInstance: Map | null;
}

export type LiveLocationUpdate = {
  longitude: number;
  latitude: number;
  accuracy?: number | null;
};

export function useMapControls({ mapInstance }: UseMapControlsProps) {
  const [zoomLevel, setZoomLevel] = useState(() => getInitialMapZoom(10));
  const userAdjustedZoomRef = useRef(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapCoordinates, setMapCoordinates] = useState<[number, number]>([120.86, 15.59]);
  const [currentBaseLayer, setCurrentBaseLayer] = useState<'satellite' | 'hybrid' | 'terrain' | 'googleSatellite' | 'googleHybrid' | 'googleTerrain'>('googleSatellite');
  const [showBaseLayerMenu, setShowBaseLayerMenu] = useState(false);
  const locationLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const trailCoordsRef = useRef<[number, number][]>([]);

  const ensureLocationLayer = useCallback((map: Map) => {
    if (locationLayerRef.current) return locationLayerRef.current;

    const source = new VectorSource();
    const layer = new VectorLayer({
      source,
      zIndex: 2000,
      style: (feature) => {
        const kind = feature.get('kind');
        if (kind === 'accuracy') {
          return new Style({
            fill: new Fill({ color: 'rgba(37, 99, 235, 0.15)' }),
            stroke: new Stroke({ color: 'rgba(37, 99, 235, 0.45)', width: 1.5 }),
          });
        }
        if (kind === 'trail') {
          return new Style({
            stroke: new Stroke({ color: 'rgba(37, 99, 235, 0.75)', width: 3 }),
          });
        }
        return new Style({
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({ color: '#2563eb' }),
            stroke: new Stroke({ color: '#ffffff', width: 2.5 }),
          }),
        });
      },
    });
    layer.set('layerType', 'currentLocation');
    // Must not intercept farm / field clicks under the accuracy circle
    layer.set('ignoreClick', true);
    layer.set('stopEvent', false);
    map.addLayer(layer);
    locationLayerRef.current = layer;
    return layer;
  }, []);

  // Remove location overlay when map unmounts / changes
  useEffect(() => {
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      const layer = locationLayerRef.current;
      if (layer && mapInstance) {
        mapInstance.removeLayer(layer);
      }
      locationLayerRef.current = null;
      trailCoordsRef.current = [];
    };
  }, [mapInstance]);

  const updateLiveLocation = useCallback(
    (
      longitude: number,
      latitude: number,
      accuracy?: number | null,
      options?: { appendTrail?: boolean; follow?: boolean }
    ) => {
      const map = mapInstance;
      if (!map) return;

      const layer = ensureLocationLayer(map);
      const source = layer.getSource();
      if (!source) return;

      if (options?.appendTrail) {
        const trail = trailCoordsRef.current;
        const last = trail[trail.length - 1];
        if (!last || last[0] !== longitude || last[1] !== latitude) {
          trail.push([longitude, latitude]);
          if (trail.length > 400) trail.shift();
        }
      }

      source.clear();

      if (trailCoordsRef.current.length >= 2) {
        const line = new LineString(trailCoordsRef.current.map((c) => fromLonLat(c)));
        source.addFeature(
          new Feature({
            geometry: line,
            kind: 'trail',
          })
        );
      }

      const center3857 = fromLonLat([longitude, latitude]);
      const accuracyMeters =
        typeof accuracy === 'number' && Number.isFinite(accuracy) && accuracy > 0
          ? Math.max(accuracy, 8)
          : 40;

      const accuracyPolygon = circular([longitude, latitude], accuracyMeters, 64);
      accuracyPolygon.transform('EPSG:4326', 'EPSG:3857');
      source.addFeature(
        new Feature({
          geometry: accuracyPolygon,
          kind: 'accuracy',
        })
      );
      source.addFeature(
        new Feature({
          geometry: new Point(center3857),
          kind: 'position',
        })
      );

      setMapCoordinates([longitude, latitude]);

      if (options?.follow) {
        const view = map.getView();
        view.animate({ center: center3857, duration: 350 });
      }
    },
    [mapInstance, ensureLocationLayer]
  );

  const clearLiveLocation = useCallback(() => {
    trailCoordsRef.current = [];
    const layer = locationLayerRef.current;
    layer?.getSource()?.clear();
  }, []);

  const stopLocationWatch = useCallback(() => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const startLocationWatch = useCallback(
    (
      onUpdate: (update: LiveLocationUpdate) => void,
      options?: { appendTrail?: boolean; follow?: boolean }
    ) => {
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by this browser.');
        return false;
      }
      if (!mapInstance) {
        alert('Map is not ready yet. Please try again in a moment.');
        return false;
      }

      stopLocationWatch();
      trailCoordsRef.current = [];

      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          updateLiveLocation(longitude, latitude, accuracy, {
            appendTrail: options?.appendTrail !== false,
            follow: options?.follow !== false,
          });
          onUpdate({ longitude, latitude, accuracy });
        },
        (error) => {
          console.error('Geolocation watch error:', error);
          alert('Unable to track your location. Please check browser permissions.');
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 2000,
        }
      );
      return true;
    },
    [mapInstance, stopLocationWatch, updateLiveLocation]
  );

  // Map control functions
  const handleZoomIn = () => {
    userAdjustedZoomRef.current = true;
    setZoomLevel(prev => Math.min(prev + 1, 20));
  };
  
  const handleZoomOut = () => {
    userAdjustedZoomRef.current = true;
    setZoomLevel(prev => Math.max(prev - 1, 1));
  };

  // Get current location — zoom in and show transparent accuracy circle
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }
    if (!mapInstance) {
      alert('Map is not ready yet. Please try again in a moment.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        const map = mapInstance;
        if (!map) return;

        trailCoordsRef.current = [];
        updateLiveLocation(longitude, latitude, accuracy, { appendTrail: false, follow: false });

        const accuracyMeters =
          typeof accuracy === 'number' && Number.isFinite(accuracy) && accuracy > 0
            ? Math.max(accuracy, 8)
            : 40;
        const accuracyPolygon = circular([longitude, latitude], accuracyMeters, 64);
        accuracyPolygon.transform('EPSG:4326', 'EPSG:3857');
        const view = map.getView();
        userAdjustedZoomRef.current = true;
        view.fit(accuracyPolygon.getExtent(), {
          duration: 900,
          padding: [56, 56, 56, 56],
          maxZoom: 18,
          callback: () => {
            const z = view.getZoom();
            if (typeof z === 'number') setZoomLevel(Math.round(z));
          },
        });
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your current location. Please check your browser permissions.');
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 10000,
      }
    );
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.error('Error attempting to exit fullscreen:', err);
      });
    }
  };

  // Nudge map zoom when moving to a large / projected display (unless user already zoomed manually).
  useEffect(() => {
    const onDisplayScaleChange = (event: Event) => {
      if (!mapInstance || userAdjustedZoomRef.current) return;
      const detail = (event as CustomEvent<DisplayScaleSnapshot>).detail;
      if (!detail?.isPresentationDisplay || detail.mapZoomOffset <= 0) return;

      const view = mapInstance.getView();
      const current = view.getZoom() ?? getInitialMapZoom(10);
      const target = Math.min(20, Math.round(getInitialMapZoom(10)));
      if (Math.abs(current - target) >= 0.5) {
        view.animate({ zoom: target, duration: 400 });
        setZoomLevel(target);
      }
    };

    window.addEventListener(DISPLAY_SCALE_CHANGE_EVENT, onDisplayScaleChange);
    return () => window.removeEventListener(DISPLAY_SCALE_CHANGE_EVENT, onDisplayScaleChange);
  }, [mapInstance]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Resize map when entering/exiting fullscreen
      setTimeout(() => {
        if (mapInstance) {
          mapInstance.updateSize();
        }
      }, 100);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [mapInstance]);

  // Handle zoom level changes
  useEffect(() => {
    if (mapInstance) {
      const currentZoom = mapInstance.getView().getZoom();
      if (Math.abs((currentZoom || 7) - zoomLevel) > 0.5) {
        mapInstance.getView().animate({
          zoom: zoomLevel,
          duration: 300
        });
      }
    }
  }, [zoomLevel, mapInstance]);

  // Handle base layer menu toggle
  const toggleBaseLayerMenu = () => {
    setShowBaseLayerMenu(!showBaseLayerMenu);
  };

  const handleBaseLayerChange = (layer: 'satellite' | 'hybrid' | 'terrain' | 'googleSatellite' | 'googleHybrid' | 'googleTerrain') => {
    setCurrentBaseLayer(layer);
    setShowBaseLayerMenu(false);
  };

  return {
    zoomLevel,
    setZoomLevel,
    isFullscreen,
    mapCoordinates,
    setMapCoordinates,
    currentBaseLayer,
    showBaseLayerMenu,
    handleZoomIn,
    handleZoomOut,
    getCurrentLocation,
    startLocationWatch,
    stopLocationWatch,
    clearLiveLocation,
    updateLiveLocation,
    toggleFullscreen,
    toggleBaseLayerMenu,
    handleBaseLayerChange
  };
}
